import { TimeObject } from 'src/common/ObjDefinition'

// @ts-ignore
const crypto_ = window.crypto || window.webkitCrypto || window.mozCrypto || window.oCrypto || window.msCrypto

const MAP_EXP = {
  Mac: /(mac os x)\s+([\w_]+)/,
  Windows: /(windows nt)\s+([\w.]+)/,
  Ios: /(i(?:pad|phone|pod)).*cpu(?: i(?:pad|phone|pod))? os (\d+(?:[.|_]\d+)+)/,
  Android: /(android)\s+([\d.]+)/,
  Ipad: /(ipad).*os\s([\d_]+)/,
  Iphone: /(iphone\sos)\s([\d_]+)/,
  Linux: /(x11;)\s(linux)\s(x86_64|mips64|aarch64)/,
  ChromiumOS: /(x11;)\s(cros)\s(x86_64|mips64|aarch64)\s([\w.]+)\)/
}
const UUIDCHARLIST = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
  'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '-', '_']

/**
 * 获取浏览器操作系统平台信息 [OSname][version][-WeChat[version]]
 */
const getPlatform = (): string => {
  const ua = navigator.userAgent.toLowerCase()
  const weChatMatch = ua.match(/(micromessenger)\/([\w.()]+)/i)
  const weChatStr = weChatMatch ? ` -WeChat${weChatMatch[2]}` : ""
  let romVersion: string
  for (const key in MAP_EXP) {
    if (Object.prototype.hasOwnProperty.call(MAP_EXP, key)) {
      const uaMatch = ua.match(MAP_EXP[key])
      if (uaMatch) {
        if (key === 'Ios') {
          romVersion = uaMatch[2].replace(/_/g, '.')
        } else if (key === 'Linux') {
          romVersion = uaMatch[3]
        } else if (key === 'ChromiumOS') {
          romVersion = uaMatch[4]
        } else {
          romVersion = uaMatch[2]
        }
        return `${key} ${romVersion}${weChatStr}`
      }
    }
  }
  return 'unknown'
}

/**
 * 获取浏览器信息  [browserName]-[version]
 */
const getBrowserVersion = (): string => {
  const matchArray = navigator.userAgent.match(/(chrome|CriOS|safari|firefox|microMessenger)\/(\d+(\.\d+)*)/i)
  if (matchArray && matchArray.length > 2) {
    let version = matchArray[2]
    if (/safari/ig.test(matchArray[1])) {
      const result = /.*Version\/([\d\.]+)\s.*/ig.exec(navigator.userAgent)
      if (result && result.length === 2) {
        version = result[1]
      }
    }
    return `${matchArray[1] === 'CriOS' ? 'Chrome' : matchArray[1]}-${version}`
  }
  return matchArray && matchArray.length > 1 ? matchArray[1] : ""
}

const module_ = 'CommonUtil'

class CommonUtil {
  private uuidCount: number
  private deviceID: string
  private timeDiff: number
  private platform: string
  private browserVersion: string

  constructor() {
    this.uuidCount = 0
    this.timeDiff = 0
    this.platform = getPlatform()
    this.browserVersion = getBrowserVersion()
  }

  public async sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const delayTimer = setTimeout(() => {
        clearTimeout(delayTimer)
        resolve()
      }, ms)
    })
  }

  public getDeviceID(): string {
    if (!this.deviceID) {
      this.deviceID = this.generateStandardUuid()
      return this.deviceID
    }
    return this.deviceID
  }

  /**
   * 生成标准的uuid的字符串
   * @returns uuid字符串
   */
  public generateStandardUuid(): string {
    let uuid: string
    if (crypto_) {
      // @ts-ignore
      uuid = typeof crypto_.randomUUID === 'function' ? crypto_.randomUUID() : ''
    }

    if (!uuid) {
      let time = new Date().getTime();
      if (window.performance && typeof window.performance.now === "function") {
        time += performance.now(); // use high-precision timer if available
      }
      uuid = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, () => {
        const rand = (time + this.getRandom() * 16) % 16 | 0;
        time = Math.floor(time / 16);
        return rand.toString(16);
      });
    }
    return uuid
  }

  /**
 * 生成0-1的安全随机数
 * @returns  安全随机数数组
 */
  public getRandom(): number {
    return this.getRandomArray(1)[0] / 256
  }

  /**
   * 生成用户相关随机字符串
   * @param userNo 用户No
   * @returns 随机字符串
   */
  public generateUuid(userNo?: number): string {
    let pre = ''
    const arr = this.getRandomArray(33)
    const randomStrLength = userNo ? 22 : 33

    for (let index = 0; index < randomStrLength; index++) {
      pre += UUIDCHARLIST[Math.round(arr[index] % 64)]
    }

    let userNoStr = ''
    for (let loop = 0; loop < 33 - randomStrLength; loop++) {
      userNoStr += UUIDCHARLIST[userNo & 0x3F]
      userNo = userNo >> 6
    }

    let uuidCountStr = this.uuidCount.toString(16)
    for (let index = 3 - uuidCountStr.length; index > 0; index--) {
      uuidCountStr = '0' + uuidCountStr
    }
    this.uuidCount > 0xFFE ? this.uuidCount = 0 : this.uuidCount++
    return `${pre}${userNoStr}${uuidCountStr}`
  }

  /**
   * 产生固定长度的随机字符串
   * @param length 随机字符串长度
   * @param rangeUpLimit 随机字符的取值范围
   * @returns 随机字符串
   */
  public generateRandomId(length: number, rangeUpLimit = 62): string {
    let randomId = ''
    const arr = this.getRandomArray(length)
    for (let index = 0; index < length; index++) {
      randomId += UUIDCHARLIST[Math.round(arr[index] % rangeUpLimit)]
    }
    return randomId
  }

  /**
   * 获取平台信息
   */
  public getPlatform(): string {
    return this.platform
  }

  public getBrowserVersion(): string {
    return this.browserVersion
  }

  public syncLocalTime(serverTime: string): void {
    try {
      if (serverTime) {
        console.debug(module_, `syncLocalTime, serverTime: ${serverTime}`)
        const serverTimeMillseconds = new Date(serverTime).getTime()
        this.timeDiff = new Date().getTime() - serverTimeMillseconds
        console.debug(module_, `syncLocalTime, timeDiff: ${this.timeDiff}`)
      }
    } catch (error) {
      console.error(module_, `syncLocalTime error: ${error}`)
    }
  }

  public getTimeDiff(): number {
    return this.timeDiff
  }

  public getCurrentLocalTimestamp(): number {
    return new Date().getTime()
  }

  public getCurrentSyncTimestamp(): number {
    if (Math.abs(this.timeDiff) >= 60 * 60 * 1000) {
      return new Date().getTime() - this.timeDiff
    }
    return new Date().getTime()
  }

  /**
   * 日期格式化
   *
   * @returns TimeObject
   * @memberof CommonUtil
   */
  public getCurrentTimeInfo(): TimeObject {
    const date = new Date(this.getCurrentLocalTimestamp())
    const currentYear = date.getFullYear()
    const month = date.getMonth() + 1
    const currentMonth = month < 10 ? `0${month}` : month
    const day = date.getDate()
    const currentDay = day < 10 ? `0${day}` : day
    const hour = date.getHours()
    const currentHour = hour < 10 ? `0${hour}` : hour
    const minute = date.getMinutes()
    const currentMinute = minute < 10 ? `0${minute}` : minute
    const second = date.getSeconds()
    const currentSecond = second < 10 ? `0${second}` : second
    const milliseconds = date.getMilliseconds()
    const currentMilliseconds = `${milliseconds + 1000}`.substring(1)
    const zoneOffset = date.getTimezoneOffset() / 60
    const zoneFlag = zoneOffset <= 0 ? '+' : '-'
    const zoneValue = zoneOffset >= 0 ? zoneOffset : -zoneOffset
    const currentZoneOffset = zoneFlag + (zoneValue < 10 ? `0${zoneValue}` : `${zoneValue}`) + ':00'
    return {
      year: `${currentYear}`,
      month: `${currentMonth}`,
      day: `${currentDay}`,
      hour: `${currentHour}`,
      min: `${currentMinute}`,
      sec: `${currentSecond}`,
      millsec: currentMilliseconds,
      zoneOff: currentZoneOffset
    }
  }

  public adjustTimeByServerTime(originalTime: number): number {
    return originalTime - this.timeDiff
  }

  public convertString2Timestamp(time: string): number {
    return new Date(time).getTime()
  }

  public generateStreamId(): string {
    return '2xxxxxxx-8xxx-xxxx-yxxx-xxxx'.replace(/[xy]/g, (ch) => {
      const num = (16 * this.getRandom()) | 0
      return (ch == 'x' ? num : (3 & num) | 8).toString(16)
    })
  }

  public isWKWebview(): boolean {
    return /I.*-WeChat.*/ig.test(this.platform)
  }

  public isAppleDevice(): boolean {
    return /^(Mac|I).*/ig.test(this.platform)
  }

  public isMobileDevice(): boolean {
    return !/^(Mac|Windows).*/ig.test(this.platform)
  }

  public isTencentX5BrowserKernel(): boolean {
    return /.*(tbs|xweb).*/ig.test(navigator.userAgent)
  }

  public isMobileWeChatBrowser(): boolean {
    return this.isMobileDevice() && /.*MicroMessenger.*/ig.test(navigator.userAgent)
  }

  public dnsRequestEncode(dnsQuery: any): string {
    const QUERY_FLAG = 0
    const headerBuffer = new DataView(new ArrayBuffer(12))
    const flags = (dnsQuery.flags || 0) & 32767
    headerBuffer.setUint16(0, dnsQuery.id || 0)
    headerBuffer.setUint16(2, flags | QUERY_FLAG)
    headerBuffer.setUint16(4, dnsQuery.questions.length)
    headerBuffer.setUint16(6, 0)
    headerBuffer.setUint16(8, 0)
    headerBuffer.setUint16(10, 0)
    const domainList = dnsQuery.questions[0].name.split('.')
    const domainUint8array = new Uint8Array(domainList.length + dnsQuery.questions[0].name.replace(/\./ig, '').length + 1)
    const encoder = new TextEncoder()
    let offset = 0
    for (let i = 0; i < domainList.length; i++) {
      domainUint8array[i + offset] = domainList[i].length
      domainUint8array.set(encoder.encode(domainList[i]), i + 1 + offset)
      offset += domainList[i].length
    }
    domainUint8array[domainUint8array.byteLength - 1] = 0

    const questionBuffer = new DataView(new ArrayBuffer(4))
    questionBuffer.setUint16(0, 1)
    questionBuffer.setUint16(2, 1)

    const concateBuff = new Uint8Array(domainUint8array.byteLength + headerBuffer.byteLength + questionBuffer.byteLength)
    concateBuff.set(new Uint8Array(headerBuffer.buffer), 0)
    concateBuff.set(domainUint8array, headerBuffer.byteLength)
    concateBuff.set(new Uint8Array(questionBuffer.buffer), headerBuffer.byteLength + domainUint8array.byteLength)
    return window.btoa(new TextDecoder().decode(concateBuff)).replace('/=/ig', '')
  }

  public getAppUrl(): string {
    const { origin, pathname } = window.location
    return `${origin}${pathname}`
  }

  public getValue(value: any, defaultValue: any): any {
    return value || defaultValue
  }

  public setValue(value: number, defaultValue: number): number {
    if (!value || value < 0) {
      return defaultValue
    }
    return value
  }

  public createLoadingMask(): HTMLElement {
    const loadingElement = document.createElement('div')
    loadingElement.setAttribute('id', 'loadingMask')
    loadingElement.style.display = "block"
    loadingElement.style.width = "100%"
    loadingElement.style.height = "100%"
    loadingElement.style.position = "absolute"
    loadingElement.style['z-index'] = 99
    loadingElement.style["background"] = "rgba(0,0,0,0.8) url(data:image/jpeg;base64,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) no-repeat center/80px"
    return loadingElement
  }

  // immediate 表示第一次是否立即执行 
  public debounce(handler: any, wait = 500, immediate: boolean) {
    let timer = null
    return {
      exec: (...args) => {
        if (timer) {
          clearTimeout(timer)
          timer = null
        }
        if (immediate && !timer) {
          handler.apply(this, args)
        }

        timer = setTimeout(() => {
          handler.apply(this, args)
        }, wait)
      },
      cancel: () => {
        clearTimeout(timer)
        timer = null
      }
    }
  }

  /**
  * ipv4/ipv6地址判断
  * @param ip ip字符串
  * @returns boolean 是否为ipv6
  */
  public isIpv6(ip: string): boolean {
    if (!ip || /^(\d{1,3}\.){3}\d{1,3}$/ig.test(ip)) {
      return false
    }
    return true
  }

  /**
  * 生成安全随机数组
  * @param length 长度
  * @returns  安全随机数数组
  */
  private getRandomArray(length: number): Uint8Array {
    const arr = new Uint8Array(length)
    if (crypto_) {
      crypto_.getRandomValues(arr)
    }
    return arr
  }
}

const commonUtil = new CommonUtil()
export default commonUtil
